// ===================================================================
// KAIXO - Modal para promocionar un latido (crear Boost)
// ===================================================================
// Flujo:
//   1. El usuario (owner del canal) pulsa "Impulsar" en un post
//   2. Este modal se abre con:
//      - Preview del post
//      - Presupuesto (slider + input)
//      - Duración (días)
//      - Opción de override de targeting
//      - Estimación en vivo (llamada a /estimate)
//   3. Al confirmar, llama a /boosts y pasa al estado pagado
//   (en MVP el pago va pendiente hasta que Stripe esté listo)
// ===================================================================

import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Icons } from '../Icons/Icons';
import { boostEstimate, boostCreate, boostGetConfig } from '../../services/boostsApi';
import { getInterestLabel } from '../../constants/interests';
import { COUNTRIES_LIST, getRegionsForCountry, nativeName } from '../../constants/countries';
import InterestPickerModal from '../InterestPicker/InterestPickerModal';
import useModalClose from '../../hooks/useModalClose';
import './BoostCreateModal.css';

const LANGUAGES = [
    'español', 'inglés', 'francés', 'alemán', 'portugués', 'italiano',
    'euskera', 'chino', 'japonés', 'árabe', 'hindi', 'ruso',
    'coreano', 'turco', 'indonesio'
];

const I18N = {
    español: {
        title: 'Promocionar Latido', close: 'Cerrar', cancel: 'Cancelar',
        budget: 'Presupuesto', duration: 'Duración', day: 'día', days: 'días',
        customAudience: 'Personalizar público objetivo (por defecto usa el del canal)',
        languages: 'Idiomas', countries: 'Países', addCountry: '+ Añadir país',
        regions: 'Regiones', addRegion: '+ Añadir región',
        interests: 'Intereses', selectInterests: '+ Seleccionar intereses',
        cpmTier: 'Categoría CPM', cpm: 'CPM',
        willReach: 'Llegarás a aprox.', people: 'personas',
        estImpressions: 'Impresiones estimadas',
        currentAudience: 'Público objetivo actual',
        users: 'usuarios',
        calculating: 'Calculando alcance...',
        creating: 'Creando...', boostFor: 'Promocionar por'
    },
    inglés: {
        title: 'Boost Heartbeat', close: 'Close', cancel: 'Cancel',
        budget: 'Budget', duration: 'Duration', day: 'day', days: 'days',
        customAudience: 'Custom audience (default: channel\'s)',
        languages: 'Languages', countries: 'Countries', addCountry: '+ Add country',
        regions: 'Regions', addRegion: '+ Add region',
        interests: 'Interests', selectInterests: '+ Select interests',
        cpmTier: 'CPM tier', cpm: 'CPM',
        willReach: 'Approx. reach', people: 'people',
        estImpressions: 'Estimated impressions',
        currentAudience: 'Current target audience',
        users: 'users',
        calculating: 'Calculating reach...',
        creating: 'Creating...', boostFor: 'Boost for'
    },
    francés: {
        title: 'Promouvoir Battement', close: 'Fermer', cancel: 'Annuler',
        budget: 'Budget', duration: 'Durée', day: 'jour', days: 'jours',
        customAudience: 'Public personnalisé (par défaut: celui de la chaîne)',
        languages: 'Langues', countries: 'Pays', addCountry: '+ Ajouter pays',
        regions: 'Régions', addRegion: '+ Ajouter région',
        interests: 'Intérêts', selectInterests: '+ Sélectionner intérêts',
        cpmTier: 'Catégorie CPM', cpm: 'CPM',
        willReach: 'Tu atteindras env.', people: 'personnes',
        estImpressions: 'Impressions estimées',
        currentAudience: 'Public cible actuel',
        users: 'utilisateurs',
        calculating: 'Calcul de la portée...',
        creating: 'Création...', boostFor: 'Promouvoir pour'
    },
    alemán: {
        title: 'Heartbeat bewerben', close: 'Schließen', cancel: 'Abbrechen',
        budget: 'Budget', duration: 'Dauer', day: 'Tag', days: 'Tage',
        customAudience: 'Zielgruppe anpassen (Standard: die des Kanals)',
        languages: 'Sprachen', countries: 'Länder', addCountry: '+ Land hinzu',
        regions: 'Regionen', addRegion: '+ Region hinzu',
        interests: 'Interessen', selectInterests: '+ Interessen wählen',
        cpmTier: 'CPM-Stufe', cpm: 'CPM',
        willReach: 'Erreichst ca.', people: 'Personen',
        estImpressions: 'Geschätzte Impressionen',
        currentAudience: 'Aktuelle Zielgruppe',
        users: 'Nutzer',
        calculating: 'Reichweite wird berechnet...',
        creating: 'Erstelle...', boostFor: 'Bewerben für'
    },
    portugués: {
        title: 'Promover Batimento', close: 'Fechar', cancel: 'Cancelar',
        budget: 'Orçamento', duration: 'Duração', day: 'dia', days: 'dias',
        customAudience: 'Público personalizado (padrão: o do canal)',
        languages: 'Idiomas', countries: 'Países', addCountry: '+ Adicionar país',
        regions: 'Regiões', addRegion: '+ Adicionar região',
        interests: 'Interesses', selectInterests: '+ Selecionar interesses',
        cpmTier: 'Categoria CPM', cpm: 'CPM',
        willReach: 'Vais chegar a aprox.', people: 'pessoas',
        estImpressions: 'Impressões estimadas',
        currentAudience: 'Público-alvo atual',
        users: 'utilizadores',
        calculating: 'A calcular alcance...',
        creating: 'A criar...', boostFor: 'Promover por'
    },
    italiano: {
        title: 'Promuovi Battito', close: 'Chiudi', cancel: 'Annulla',
        budget: 'Budget', duration: 'Durata', day: 'giorno', days: 'giorni',
        customAudience: 'Pubblico personalizzato (default: quello del canale)',
        languages: 'Lingue', countries: 'Paesi', addCountry: '+ Aggiungi paese',
        regions: 'Regioni', addRegion: '+ Aggiungi regione',
        interests: 'Interessi', selectInterests: '+ Seleziona interessi',
        cpmTier: 'Categoria CPM', cpm: 'CPM',
        willReach: 'Raggiungerai circa', people: 'persone',
        estImpressions: 'Impressioni stimate',
        currentAudience: 'Pubblico target attuale',
        users: 'utenti',
        calculating: 'Calcolo portata...',
        creating: 'Creazione...', boostFor: 'Promuovi per'
    },
    euskera: {
        title: 'Latidoa promozionatu', close: 'Itxi', cancel: 'Utzi',
        budget: 'Aurrekontua', duration: 'Iraupena', day: 'egun', days: 'egun',
        customAudience: 'Audientzia pertsonalizatu (lehenetsia: kanalarena)',
        languages: 'Hizkuntzak', countries: 'Herrialdeak', addCountry: '+ Herrialdea gehitu',
        regions: 'Eskualdeak', addRegion: '+ Eskualdea gehitu',
        interests: 'Interesak', selectInterests: '+ Interesak hautatu',
        cpmTier: 'CPM kategoria', cpm: 'CPM',
        willReach: 'Iritsiko zara gutxi gora-behera', people: 'pertsonarengana',
        estImpressions: 'Inpresio kopurua',
        currentAudience: 'Egungo audientzia',
        users: 'erabiltzaile',
        calculating: 'Irismena kalkulatzen...',
        creating: 'Sortzen...', boostFor: 'Promozionatu'
    }
};
function pickL() {
    try {
        const u = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
        return I18N[u?.language] || I18N.inglés;
    } catch { return I18N.inglés; }
}

function BoostCreateModal({ message, channel, currentUser, onClose, onCreated }) {
    useModalClose(onClose);
    const L = pickL();
    const [budget, setBudget] = useState(10);          // USD
    const [duration, setDuration] = useState(3);        // días
    const [overrideTargeting, setOverrideTargeting] = useState(false);
    const [targetCountries, setTargetCountries] = useState(channel?.targetCountries || []);
    const [targetRegions, setTargetRegions] = useState(channel?.targetRegions || []);
    const [targetLanguages, setTargetLanguages] = useState(channel?.targetLanguages || []);
    const [targetInterests, setTargetInterests] = useState(channel?.targetInterests || []);
    const [estimate, setEstimate] = useState(null);
    const [estimating, setEstimating] = useState(false);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState('');
    const [publicConfig, setPublicConfig] = useState(null);
    const [showInterestPicker, setShowInterestPicker] = useState(false);

    const lang = currentUser?.language || 'español';

    // Cargar config pública (precios, límites)
    useEffect(() => {
        let cancelled = false;
        boostGetConfig()
            .then(({ data }) => { if (!cancelled) setPublicConfig(data); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    // Targeting efectivo (si override activo, el del form; si no, el del canal)
    const effectiveTargeting = useMemo(() => overrideTargeting
        ? { targetCountries, targetRegions, targetLanguages, targetInterests }
        : {
            targetCountries: channel?.targetCountries || [],
            targetRegions: channel?.targetRegions || [],
            targetLanguages: channel?.targetLanguages || [],
            targetInterests: channel?.targetInterests || []
        }
    , [overrideTargeting, targetCountries, targetRegions, targetLanguages, targetInterests, channel]);

    // Re-estimar cuando cambia el presupuesto, duración o targeting
    useEffect(() => {
        if (budget < (publicConfig?.limits?.minBoostBudgetUsd || 5)) return;
        let cancelled = false;
        setEstimating(true);
        const timer = setTimeout(async () => {
            try {
                const { data } = await boostEstimate({
                    targeting: effectiveTargeting,
                    budgetUsd: budget,
                    durationDays: duration
                });
                if (!cancelled) setEstimate(data);
            } catch (e) {
                if (!cancelled) setError(e.response?.data?.error || e.message);
            } finally {
                if (!cancelled) setEstimating(false);
            }
        }, 400);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [budget, duration, effectiveTargeting, publicConfig]);

    const tierColor = {
        basic: '#10B981', medium: '#F59E0B',
        advanced: '#8B5CF6', premium: '#EC4899'
    }[estimate?.tier] || '#666';
    const tierLabel = {
        basic: 'Básico',    medium: 'Medio',
        advanced: 'Avanzado', premium: 'Premium'
    }[estimate?.tier] || '';

    const handleCreate = async () => {
        setCreating(true);
        setError('');
        try {
            const payload = {
                messageId: String(message._id),
                budgetUsd: budget,
                durationDays: duration,
                targeting: overrideTargeting ? effectiveTargeting : undefined
            };
            const { data } = await boostCreate(payload);
            if (onCreated) onCreated(data.boost);
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        }
        setCreating(false);
    };

    return ReactDOM.createPortal(
        <div className="bst-overlay" onClick={onClose}>
            <div className="bst-modal" onClick={e => e.stopPropagation()}>
                <div className="bst-header">
                    <h3>{L.title}</h3>
                    <button className="bst-close" onClick={onClose} aria-label={L.close}>
                        <Icons.Close size={18} />
                    </button>
                </div>

                <div className="bst-body">
                    {/* Preview del post */}
                    <div className="bst-preview">
                        {message.mediaType === 'image' && message.mediaUrl && (
                            <img src={message.mediaUrl} alt="" className="bst-preview-img" />
                        )}
                        <p className="bst-preview-text">{(message.originalText || message.text || '').slice(0, 140)}</p>
                    </div>

                    {/* Presupuesto */}
                    <label className="bst-label">
                        {L.budget}: <strong>${budget.toFixed(2)}</strong>
                    </label>
                    <input
                        type="range"
                        min={publicConfig?.limits?.minBoostBudgetUsd || 5}
                        max={500}
                        step={1}
                        value={budget}
                        onChange={e => setBudget(parseFloat(e.target.value))}
                        className="bst-slider"
                    />
                    <div className="bst-slider-marks">
                        <span>$5</span><span>$50</span><span>$500</span>
                    </div>

                    {/* Duración */}
                    <label className="bst-label">
                        {L.duration}: <strong>{duration} {duration === 1 ? L.day : L.days}</strong>
                    </label>
                    <input
                        type="range"
                        min={publicConfig?.limits?.minDurationDays || 1}
                        max={publicConfig?.limits?.maxDurationDays || 30}
                        step={1}
                        value={duration}
                        onChange={e => setDuration(parseInt(e.target.value, 10))}
                        className="bst-slider"
                    />

                    {/* Override de targeting */}
                    <label className="bst-checkbox">
                        <input
                            type="checkbox"
                            checked={overrideTargeting}
                            onChange={e => setOverrideTargeting(e.target.checked)}
                        />
                        {' '}{L.customAudience}
                    </label>

                    {overrideTargeting && (
                        <div className="bst-targeting">
                            <label className="bst-mini-label">{L.languages}</label>
                            <div className="bst-chips">
                                {LANGUAGES.map(l => (
                                    <button
                                        key={l}
                                        type="button"
                                        className={`bst-chip ${targetLanguages.includes(l) ? 'active' : ''}`}
                                        onClick={() => setTargetLanguages(prev =>
                                            prev.includes(l) ? prev.filter(x => x !== l) : [...prev, l]
                                        )}
                                    >
                                        {l}
                                    </button>
                                ))}
                            </div>

                            <label className="bst-mini-label">{L.countries}</label>
                            <select value="" onChange={e => {
                                const v = e.target.value;
                                if (!v) return;
                                setTargetCountries(p => p.includes(v) ? p : [...p, v]);
                            }}>
                                <option value="">{L.addCountry}</option>
                                {COUNTRIES_LIST.filter(c => !targetCountries.includes(c.id)).map(c => (
                                    <option key={c.id} value={c.id}>{c.native}</option>
                                ))}
                            </select>
                            {targetCountries.length > 0 && (
                                <div className="bst-chips">
                                    {targetCountries.map(c => (
                                        <button key={c} type="button" className="bst-chip active"
                                            onClick={() => {
                                                setTargetCountries(p => p.filter(x => x !== c));
                                                const r = getRegionsForCountry(c);
                                                if (r.length) setTargetRegions(p => p.filter(x => !r.includes(x)));
                                            }}>{nativeName(c)} ×</button>
                                    ))}
                                </div>
                            )}

                            {targetCountries.length > 0 && (
                                <>
                                    <label className="bst-mini-label">{L.regions}</label>
                                    <select value="" onChange={e => {
                                        const v = e.target.value;
                                        if (!v) return;
                                        setTargetRegions(p => p.includes(v) ? p : [...p, v]);
                                    }}>
                                        <option value="">{L.addRegion}</option>
                                        {targetCountries.flatMap(country =>
                                            getRegionsForCountry(country).map(region => ({ country, region }))
                                        ).filter(({ region }) => !targetRegions.includes(region))
                                         .map(({ country, region }) => (
                                            <option key={`${country}|${region}`} value={region}>
                                                {nativeName(country)}: {region}
                                            </option>
                                         ))}
                                    </select>
                                    {targetRegions.length > 0 && (
                                        <div className="bst-chips">
                                            {targetRegions.map(r => (
                                                <button key={r} type="button" className="bst-chip active"
                                                    onClick={() => setTargetRegions(p => p.filter(x => x !== r))}>
                                                    {r} ×
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}

                            <label className="bst-mini-label">{L.interests}</label>
                            <button
                                type="button"
                                className="bst-select-button"
                                onClick={() => setShowInterestPicker(true)}
                                style={{ width: '100%', textAlign: 'left', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e0e0e0', background: '#fff', cursor: 'pointer', fontSize: 14 }}
                            >
                                {L.selectInterests}
                            </button>
                            {targetInterests.length > 0 && (
                                <div className="bst-chips">
                                    {targetInterests.map(k => (
                                        <button key={k} type="button" className="bst-chip active"
                                            onClick={() => setTargetInterests(p => p.filter(x => x !== k))}>
                                            {getInterestLabel(k, lang)} ×
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Estimación */}
                    {estimate && !estimating && (
                        <div className="bst-estimate">
                            <div className="bst-estimate-row">
                                <span>{L.cpmTier}:</span>
                                <span className="bst-tier" style={{ background: tierColor }}>
                                    {tierLabel}
                                </span>
                            </div>
                            <div className="bst-estimate-row">
                                <span>{L.cpm}:</span>
                                <strong>${estimate.cpmUsd.toFixed(2)}</strong>
                            </div>
                            <div className="bst-estimate-row bst-estimate-main">
                                <span>{L.willReach}:</span>
                                <strong>{estimate.estimatedReach.toLocaleString()} {L.people}</strong>
                            </div>
                            <div className="bst-estimate-row">
                                <span>{L.estImpressions}:</span>
                                <strong>{estimate.estimatedImpressions.toLocaleString()}</strong>
                            </div>
                            <div className="bst-estimate-row bst-estimate-hint">
                                {L.currentAudience}: {estimate.poolSize.toLocaleString()} {L.users}
                            </div>
                        </div>
                    )}
                    {estimating && (
                        <div className="bst-estimate">
                            <p className="bst-center">{L.calculating}</p>
                        </div>
                    )}

                    {error && <div className="bst-error">{error}</div>}
                </div>

                <div className="bst-footer">
                    <button className="bst-btn-cancel" onClick={onClose} disabled={creating}>
                        {L.cancel}
                    </button>
                    <button
                        className="bst-btn-create"
                        onClick={handleCreate}
                        disabled={creating || !estimate}
                    >
                        {creating ? L.creating : `${L.boostFor} $${budget.toFixed(2)}`}
                    </button>
                </div>
            </div>
            {showInterestPicker && (
                <InterestPickerModal
                    value={targetInterests}
                    max={20}
                    language={lang}
                    accent="#00b4d8"
                    onSave={(next) => setTargetInterests(next)}
                    onClose={() => setShowInterestPicker(false)}
                />
            )}
        </div>,
        document.body
    );
}

export default BoostCreateModal;
